import type { ChatSession } from '../types';

/**
 * `GET /api/conversations/sessions` is a summary list: it carries session
 * metadata and no message bodies, so that its cost stays flat as a user's
 * history grows. Bodies come from the per-session endpoint instead.
 *
 * That makes an empty `messages` array on a remote session meaningless rather
 * than authoritative — it says "not included here", never "this conversation
 * is empty". Anything merging a summary into local state has to go through
 * here, or reloading the list silently blanks every conversation on screen.
 */
export function keepMessagesAgainstSummary(
  remote: ChatSession,
  local: ChatSession | undefined,
): ChatSession {
  if (remote.messages?.length) return remote;
  if (!local?.messages?.length) return remote;
  return { ...remote, messages: local.messages };
}

/**
 * Whether the local copy of a conversation outranks the server's snapshot.
 *
 * Two ways it can. The server may simply not have the turn yet, which the
 * output check covers. Or the snapshot may predate this tab's last write: the
 * worker checkpoints while it generates, so a snapshot fetched mid-turn *does*
 * carry output and would otherwise win over the finished turn the user is
 * reading — replacing what they just watched arrive with a half-written copy
 * of it.
 *
 * Ties go to the remote, so another tab's work still lands here.
 */
export function preferLocalMessages(input: {
  localHasOutput: boolean;
  remoteHasOutput: boolean;
  localUpdatedAt?: number;
  remoteUpdatedAt?: number;
}): boolean {
  if (input.localHasOutput && !input.remoteHasOutput) return true;
  return (input.remoteUpdatedAt || 0) < (input.localUpdatedAt || 0);
}

/**
 * Whether a session the summary list does not carry may still call itself
 * streaming.
 *
 * The list is the server's complete account of a user's conversations, so a
 * session missing from it is one the server holds no row for — created in this
 * tab moments ago, or since deleted. The server has no opinion to merge, which
 * leaves the merge above with nothing to take `isStreaming` from.
 *
 * Left alone, a flag stranded there by a crashed tab or a turn that died mid
 * stream can never be cleared again — and that flag is what arms the refresh
 * poll, so a single orphan pins a three-second fetch-and-rerender loop for the
 * rest of the session. Only a generation this tab is still driving outranks the
 * server's silence.
 */
export function clearOrphanedStreamingFlag(
  session: ChatSession,
  isGeneratingHere: boolean,
): ChatSession {
  if (!session.isStreaming || isGeneratingHere) return session;
  return { ...session, isStreaming: false };
}

/**
 * The index of a session's newest assistant message, or -1 when it has none.
 *
 * Shared rather than reimplemented: `applySessionLivenessToTurn` below needs
 * it to know which message the session's own `isStreaming` belongs to, and
 * `isLocalCopyStreaming` (`streamingSessionHydration.ts`) needs the same
 * message to decide whether a tab's restored cache still reads as streaming
 * — two callers asking the same question about the same array (review of
 * 03aba39e).
 */
export function findNewestAssistantMessageIndex(messages: { role: string }[]): number {
  for (let i = messages.length - 1; i >= 0; i -= 1) {
    if (messages[i].role === 'assistant') return i;
  }
  return -1;
}

/**
 * Carry the session's liveness onto the turn the snapshot was taken from.
 *
 * The server persists messages; it does not persist `isStreaming`, which only
 * ever exists on the copy the streaming tab is writing. So every copy that
 * arrives through this refresh — a second tab, a reload, this tab after the
 * turn moved to another one — describes a turn that is very much still running
 * as one that has finished, and the presentation layer publishes its
 * deliverables on that basis.
 *
 * Measured live on 2026-09-10: a turn published its whole delivery — the
 * program file, the code listing, 下载未验证版本, 安全校验报告, 导出交付包 — at
 * 23:28:17, and by 23:28:39 every bit of it was gone and the turn was back at
 * 「修复问题 · 1 轮修复」, which then ran for another three minutes.
 *
 * The session row does know, so hand that down to the newest turn. Both
 * directions: the flag has to come off again when the server says the run is
 * over, or a snapshot taken mid-run would leave a spinner up for good.
 */
export function applySessionLivenessToTurn(session: ChatSession): ChatSession {
  const messages = session.messages;
  if (!messages?.length) return session;

  const live = session.isStreaming === true;
  const index = findNewestAssistantMessageIndex(messages);
  if (index < 0) return session;
  if (Boolean(messages[index].isStreaming) === live) return session;

  const next = messages.slice();
  next[index] = { ...next[index], isStreaming: live };
  return { ...session, messages: next };
}
